import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { ConfirmDialogProvider, ErrorBoundary, ProtectedRoute, ToastProvider } from "@/components/shared";
import { featureFlags } from "@/config/featureFlags";
import { getMsalConfig } from "@/config/msal";
import { getRuntimeConfig } from "@/config/runtime";
import { AppProvider } from "@/context/AppContext";
import { AuthContextProvider, useAuth } from "@/context/AuthContext";
import { useGoogleAuth } from "@/context/auth/GoogleAuthProvider";
import { useMsalAuth } from "@/context/auth/MsalAuthProvider";
import { NotificationProvider } from "@/context/NotificationContext";
import { OperationProvider } from "@/context/OperationContext";
import { SharedGroupChatSessionsProvider } from "@/context/SharedGroupChatSessionsContext";
import { SharedSessionsProvider } from "@/context/SharedSessionsContext";
import type { ThemeMode } from "@/context/ThemeContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { useGlobalDropGuard } from "@/hooks/useGlobalDropGuard";
import { Admin } from "@/pages/Admin";
import { AgentFlows } from "@/pages/AgentFlows";
import { Agents } from "@/pages/Agents";
import { Apps } from "@/pages/Apps";
import { MicroApps } from "@/pages/apps/MicroApps";
import { MicroAppViewer } from "@/pages/apps/MicroAppViewer";
import { PresentationsNewProject } from "@/pages/apps/presentations/NewProject";
import { PresentationsProjectDetail } from "@/pages/apps/presentations/ProjectDetail";
import { PresentationsProjectList } from "@/pages/apps/presentations/ProjectList";
import { SowiftNewProject } from "@/pages/apps/sowift/NewProject";
import { SowiftPresentations } from "@/pages/apps/sowift/Presentations";
import { SowiftProjectDetail } from "@/pages/apps/sowift/ProjectDetail";
import { SowiftProjectList } from "@/pages/apps/sowift/ProjectList";
import { Brands } from "@/pages/Brands";
import { Builder } from "@/pages/Builder";
import { Canvas } from "@/pages/Canvas";
import { Chat } from "@/pages/Chat";
// Pages
import { Dashboard } from "@/pages/Dashboard";
import { DataSourceDetail } from "@/pages/DataSourceDetail";
import { DataSources } from "@/pages/DataSources";
import { DeepResearch } from "@/pages/DeepResearch";
import { ExtensionDetail } from "@/pages/ExtensionDetail";
import { Extensions } from "@/pages/Extensions";
import { FlowDetail } from "@/pages/FlowDetail";
import { FlowEdit } from "@/pages/FlowEdit";
import { FlowRunDetail } from "@/pages/FlowRunDetail";
import { FlowRunHistory } from "@/pages/FlowRunHistory";
import { FlowRunner } from "@/pages/FlowRunner";
import { GroupChat } from "@/pages/GroupChat";
import { Help } from "@/pages/Help";
import { History } from "@/pages/History";
import { NotFound } from "@/pages/NotFound";
import { Notifications } from "@/pages/Notifications";
import { ProjectHub } from "@/pages/ProjectHub";
import { Projects } from "@/pages/Projects";
import { Settings } from "@/pages/Settings";
import { SharedChatRedirect } from "@/pages/SharedChatRedirect";
import { SharedDeepResearchRedirect } from "@/pages/SharedDeepResearchRedirect";
import { SharedGroupChatRedirect } from "@/pages/SharedGroupChatRedirect";
import { Skills } from "@/pages/Skills";
import { Tags } from "@/pages/Tags";
import { TaskDetail } from "@/pages/TaskDetail";
import { Tasks } from "@/pages/Tasks";
import { TeamDetail } from "@/pages/TeamDetail";
import { TeamHub } from "@/pages/TeamHub";
import { Teams } from "@/pages/Teams";

// ---------------------------------------------------------------------------
// Provider-specific wrappers
// ---------------------------------------------------------------------------

// MSAL instance — created at module scope (requires initRuntimeConfig() to
// have completed before this module is imported; main.tsx enforces this).
const config = getRuntimeConfig();
const msalInstance = config.authProvider === "microsoft" ? new PublicClientApplication(getMsalConfig()) : null;

/**
 * Wraps children with MsalProvider + the MSAL auth hook → AuthContext.
 */
function MsalAuthProviderWrapper({ children }: Readonly<{ children: ReactNode }>) {
  const auth = useMsalAuth();
  return <AuthContextProvider value={auth}>{children}</AuthContextProvider>;
}

/**
 * Wraps children with the Google auth hook → AuthContext.
 * Must be rendered inside `<GoogleOAuthProvider>`.
 */
function GoogleAuthProviderWrapper({ children }: Readonly<{ children: ReactNode }>) {
  const auth = useGoogleAuth();
  return <AuthContextProvider value={auth}>{children}</AuthContextProvider>;
}

/**
 * Bridge component that reads the auth context and passes the user's
 * theme preference to ThemeProvider. This exists because ThemeProvider
 * needs to be inside AuthProvider to read the profile.
 */
function ThemedApp({ children }: Readonly<{ children: React.ReactNode }>) {
  const { profile } = useAuth();
  const initialMode = (profile?.preferences?.theme as ThemeMode) || "dark";
  return <ThemeProvider initialMode={initialMode}>{children}</ThemeProvider>;
}

/**
 * The inner app tree — everything below the auth provider.
 *
 * Data-fetching providers (AppProvider, NotificationProvider, etc.) are
 * intentionally placed INSIDE ProtectedRoute so they only mount after
 * authentication has completed.  This prevents a burst of unauthenticated
 * API calls on initial page load — particularly with Google's implicit
 * OAuth flow where the token becomes available asynchronously.
 */
function AppShell() {
  return (
    <ThemedApp>
      <ToastProvider>
        <ConfirmDialogProvider>
          <BrowserRouter>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AppProvider>
                      <NotificationProvider>
                        <SharedSessionsProvider>
                          <SharedGroupChatSessionsProvider>
                            <OperationProvider>
                              <MainLayout />
                            </OperationProvider>
                          </SharedGroupChatSessionsProvider>
                        </SharedSessionsProvider>
                      </NotificationProvider>
                    </AppProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="chat" element={<Chat />} />
                <Route path="chat/shared/:token" element={<SharedChatRedirect />} />
                <Route path="chat/:sessionId" element={<Chat />} />
                {featureFlags.brainstorm && (
                  <>
                    <Route path="group-chat/shared/:token" element={<SharedGroupChatRedirect />} />
                    <Route path="group-chat" element={<GroupChat />} />
                    <Route path="group-chat/:sessionId" element={<GroupChat />} />
                  </>
                )}
                <Route path="agents" element={<Agents />} />
                {featureFlags.skillImport && <Route path="skills" element={<Skills />} />}
                <Route path="builder" element={<Builder />} />
                {featureFlags.workflows && (
                  <>
                    <Route path="flows" element={<AgentFlows />} />
                    <Route path="flows/:flowId" element={<FlowDetail />} />
                    <Route path="flows/:flowId/edit" element={<FlowEdit />} />
                    <Route path="flows/:flowId/run" element={<FlowRunner />} />
                    <Route path="flows/:flowId/runs" element={<FlowRunHistory />} />
                    <Route path="flows/:flowId/runs/:runId" element={<FlowRunDetail />} />
                  </>
                )}
                {featureFlags.autopilot && (
                  <>
                    <Route path="tasks" element={<Tasks />} />
                    <Route path="tasks/:taskId" element={<TaskDetail />} />
                  </>
                )}
                <Route path="data-sources" element={<DataSources />} />
                <Route path="data-sources/:collectionId" element={<DataSourceDetail />} />
                <Route path="history" element={<History />} />
                <Route path="extensions" element={<Extensions />} />
                <Route path="extensions/:uuid" element={<ExtensionDetail />} />
                <Route path="teams" element={<Teams />} />
                <Route path="teams/:teamId" element={<TeamHub />} />
                <Route path="teams/:teamId/manage" element={<TeamDetail />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:projectId" element={<ProjectHub />} />
                <Route path="projects/:projectId/chat" element={<Chat />} />
                <Route path="projects/:projectId/chat/:sessionId" element={<Chat />} />
                <Route path="tags" element={<Tags />} />
                {featureFlags.apps && (
                  <>
                    <Route path="apps" element={<Apps />} />
                    <Route path="apps/sowift" element={<SowiftProjectList />} />
                    <Route path="apps/sowift/new" element={<SowiftNewProject />} />
                    <Route path="apps/sowift/:projectId" element={<SowiftProjectDetail />} />
                    <Route path="apps/sowift/presentations" element={<SowiftPresentations />} />
                    <Route path="apps/presentations" element={<PresentationsProjectList />} />
                    <Route path="apps/presentations/new" element={<PresentationsNewProject />} />
                    <Route path="apps/presentations/:projectId" element={<PresentationsProjectDetail />} />
                  </>
                )}
                {featureFlags.microApps && (
                  <>
                    <Route path="apps/view" element={<MicroApps />} />
                    <Route path="apps/view/:uuid" element={<MicroAppViewer />} />
                  </>
                )}
                {featureFlags.multiBrand && <Route path="brands" element={<Brands />} />}
                {featureFlags.canvas && (
                  <>
                    <Route path="canvas" element={<Canvas />} />
                    <Route path="canvas/:sessionId" element={<Canvas />} />
                  </>
                )}
                <Route path="deep-research" element={<DeepResearch />} />
                <Route path="deep-research/shared/:token" element={<SharedDeepResearchRedirect />} />
                <Route path="deep-research/:requestId" element={<DeepResearch />} />
                <Route path="settings" element={<Settings />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="help" element={<Help />} />
                <Route path="admin/*" element={<Admin />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ConfirmDialogProvider>
      </ToastProvider>
    </ThemedApp>
  );
}

// ---------------------------------------------------------------------------
// Root App component
// ---------------------------------------------------------------------------

export function App() {
  const [queryClient] = useState(() => new QueryClient());

  // Prevent the browser from opening/downloading files dropped anywhere outside
  // an explicit dropzone.
  useGlobalDropGuard();

  const authContent =
    config.authProvider === "google" ? (
      <GoogleOAuthProvider clientId={config.googleClientId}>
        <GoogleAuthProviderWrapper>
          <AppShell />
        </GoogleAuthProviderWrapper>
      </GoogleOAuthProvider>
    ) : msalInstance ? (
      <MsalProvider instance={msalInstance}>
        <MsalAuthProviderWrapper>
          <AppShell />
        </MsalAuthProviderWrapper>
      </MsalProvider>
    ) : (
      <div>Authentication configuration error: MSAL instance could not be created.</div>
    );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>{authContent}</QueryClientProvider>
    </ErrorBoundary>
  );
}
